import { useState, useCallback, useMemo } from 'react';
import { IPersonaProps } from '@fluentui/react';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/api';
import { TimeUser } from '../../../../../api/generated/data-contracts';
import { mapUserToPersona } from '../../../../../_helpers/mapUserToPersona';
import { UseUserSearchReturn } from '../types';

export const useUserSearch = (): UseUserSearchReturn => {
    const [selectedUser, setSelectedUser] = useState<TimeUser | null>(null);

    // User search mutation
    const { mutateAsync: searchUsers, data: userSearchResults } = useMutation({
        mutationFn: (searchText: string) => ApiCalls.searchUsers({ searchText }).then(res => res.data),
        onError: error => {
            console.error('User search error:', error);
        },
    });

    // User picker functionality
    const onUserResolveSuggestions = useCallback(
        (searchText: string) => {
            if (searchText) {
                return searchUsers(searchText)
                    .then(users => users.map(mapUserToPersona))
                    .catch(error => {
                        console.error('User search failed:', error);
                        return [];
                    });
            }
            return null;
        },
        [searchUsers],
    );

    const onUserSelected = useCallback(
        (persona: IPersonaProps) => {
            if (persona && userSearchResults) {
                const user = userSearchResults.find(u => u.id === persona.itemID);
                if (user) {
                    setSelectedUser(user);
                    return null;
                }
            }
            return persona;
        },
        [userSearchResults],
    );

    const onRemoveUser = useCallback(() => {
        setSelectedUser(null);
    }, []);

    // Create persona for selected user
    const selectedUserPersona = useMemo(() => {
        if (selectedUser) {
            return mapUserToPersona(selectedUser);
        }
    }, [selectedUser]);

    return {
        selectedUser,
        setSelectedUser,
        onUserResolveSuggestions,
        onUserSelected,
        onRemoveUser,
        selectedUserPersona,
        userSearchResults,
    };
};
